import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { type ApiError, api, unwrap } from '../api/client';
import { useAccounts } from '../api/hooks';
import type {
  ApiKey,
  ApiLog,
  Items,
  OpenApiDocument,
  WebhookDelivery,
  WebhookEndpoint,
} from '../api/types';
import {
  Badge,
  Card,
  DataTable,
  Empty,
  ErrorAlert,
  Field,
  fmtDate,
  Modal,
  PageHeader,
  useToast,
} from '../components/ui';
import { buildRequest, type Endpoint, listEndpoints } from './api-console';

const EVENTS = [
  'new_comment',
  'comment_reply',
  'new_dm',
  'story_reply',
  'question_answered',
  'flow_completed',
  'message_sent',
  'automation_completed',
  'contact_created',
];
/** DEV-03: the server's own OpenAPI document rendered as a table, each row loads the console. */
function ApiDocs(props: { onPick: (e: Endpoint) => void }) {
  const doc = useQuery({
    queryKey: ['openapi'],
    queryFn: async () => {
      const r = await fetch('/v1/openapi.json');
      if (!r.ok) throw new Error(`openapi ${r.status}`);
      return (await r.json()) as OpenApiDocument;
    },
  });
  const endpoints = doc.data ? listEndpoints(doc.data) : [];
  return (
    <Card title={`API documentation${doc.data ? ` · OpenAPI ${doc.data.openapi}` : ''}`}>
      <p className="muted small">
        {doc.data?.info.description ?? 'Loading the OpenAPI document…'}{' '}
        <a href="/v1/openapi.json" target="_blank" rel="noreferrer">
          openapi.json
        </a>
      </p>
      <table className="table" data-testid="openapi-table">
        <tbody>
          {endpoints.map((e) => (
            <tr key={`${e.method} ${e.path}`}>
              <td>
                <span className="badge">{e.tag}</span>
              </td>
              <td className="mono">
                <strong>{e.method.toUpperCase()}</strong> {e.path}
              </td>
              <td className="muted">{e.summary}</td>
              <td>
                <button type="button" className="btn btn-sm" onClick={() => props.onPick(e)}>
                  Try it
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/** DEV-03 "try it": signs the request with a workspace key and shows the real response. */
function ApiConsole(props: { endpoint: Endpoint | null; initialKey: string }) {
  const [apiKey, setApiKey] = useState(props.initialKey);
  const [params, setParams] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<{ status: number; body: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: async (e: Endpoint) => {
      const req = buildRequest({ endpoint: e, apiKey, params, query, body });
      if ('error' in req) throw new Error(req.error);
      const r = await fetch(req.url, req.init);
      const text = await r.text();
      return { status: r.status, body: text };
    },
    onSuccess: (r) => {
      setError(null);
      setResult(r);
    },
    onError: (e: Error) => setError(e.message),
  });
  const e = props.endpoint;
  return (
    <Card title="Try it">
      {e ? (
        <>
          <p className="mono">
            <strong>{e.method.toUpperCase()}</strong> {e.path}{' '}
            <span className="muted">· {e.summary}</span>
          </p>
          <Field label="Workspace API key">
            <input
              className="input mono"
              value={apiKey}
              onChange={(ev) => setApiKey(ev.target.value)}
              placeholder="adm_live_…"
            />
          </Field>
          {e.params.map((p) => (
            <Field key={p} label={`Path parameter: ${p}`}>
              <input
                className="input mono"
                value={params[p] ?? ''}
                onChange={(ev) => setParams({ ...params, [p]: ev.target.value })}
              />
            </Field>
          ))}
          <Field label="Query string (optional)">
            <input
              className="input mono"
              value={query}
              onChange={(ev) => setQuery(ev.target.value)}
              placeholder="limit=20"
            />
          </Field>
          {e.hasBody ? (
            <Field label="JSON body">
              <textarea
                className="input mono"
                rows={6}
                value={body}
                onChange={(ev) => setBody(ev.target.value)}
                placeholder="{}"
              />
            </Field>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending}
            onClick={() => send.mutate(e)}
          >
            Send request
          </button>
          {error ? <div className="alert alert-error">{error}</div> : null}
          {result ? (
            <pre
              className="mono"
              data-testid="console-response"
              style={{
                background: 'var(--surface-2)',
                padding: 12,
                borderRadius: 8,
                overflow: 'auto',
                marginTop: 10,
              }}
            >
              {`HTTP ${result.status}\n${prettyJson(result.body)}`}
            </pre>
          ) : null}
        </>
      ) : (
        <p className="muted">Pick an endpoint from the documentation to call it with your key.</p>
      )}
    </Card>
  );
}

const prettyJson = (text: string): string => {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

export function ApiPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<'webhooks' | 'api' | 'logs'>('webhooks');
  const [tryEndpoint, setTryEndpoint] = useState<Endpoint | null>(null);
  const hooks = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => unwrap(api.get<Items<WebhookEndpoint>>('/portal/webhooks')),
  });
  // INT-21: an endpoint's account scope, by @username where the account is visible
  const accounts = useAccounts();
  const handleOf = (id: string): string => {
    const a = accounts.data?.items.find((x) => x.id === id);
    return a ? `@${a.username}` : id;
  };
  const keys = useQuery({
    queryKey: ['apikeys'],
    queryFn: () => unwrap(api.get<Items<ApiKey>>('/portal/api-keys')),
  });
  const logs = useQuery({
    queryKey: ['apilogs'],
    queryFn: () => unwrap(api.get<Items<ApiLog>>('/portal/api-logs')),
    enabled: tab === 'logs',
  });
  const sandbox = useQuery({
    queryKey: ['sandbox'],
    queryFn: () => unwrap(api.get<Record<string, unknown>>('/portal/sandbox')),
    enabled: tab === 'api',
  });
  const [hookForm, setHookForm] = useState<{ url: string; events: string[] }>({
    url: '',
    events: ['new_comment'],
  });
  const [secret, setSecret] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<string | null>(null);
  const deliveries = useQuery({
    queryKey: ['deliveries', deliveriesFor],
    queryFn: () =>
      unwrap(api.get<Items<WebhookDelivery>>(`/portal/webhooks/${deliveriesFor}/deliveries`)),
    enabled: deliveriesFor !== null,
  });
  // ADM-16: a failed delivery goes back to the queue
  const retryDelivery = useMutation({
    mutationFn: (deliveryId: string) =>
      unwrap(api.post(`/portal/webhooks/${deliveriesFor}/deliveries/${deliveryId}/retry`)),
    onSuccess: () => {
      toast('Delivery queued again');
      return qc.invalidateQueries({ queryKey: ['deliveries'] });
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const createHook = useMutation({
    mutationFn: () => unwrap(api.post<WebhookEndpoint>('/portal/webhooks', hookForm)),
    onSuccess: async (h) => {
      setSecret(h.secret ?? null);
      setHookForm({ url: '', events: ['new_comment'] });
      await qc.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
  const rotate = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.post<{ secret: string }>(`/portal/webhooks/${id}/rotate-secret`)),
    onSuccess: (r) => setSecret(r.secret),
  });
  const toggleHook = useMutation({
    mutationFn: (h: WebhookEndpoint) =>
      unwrap(api.patch(`/portal/webhooks/${h.id}`, { enabled: !h.enabled })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
  const deleteHook = useMutation({
    mutationFn: (id: string) => unwrap(api.del(`/portal/webhooks/${id}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  });
  // IAM-27: a key for some accounts; none picked is every account the member can reach
  const [keyAccounts, setKeyAccounts] = useState<string[]>([]);
  const createKey = useMutation({
    mutationFn: () =>
      unwrap(
        api.post<{ key: string }>('/portal/api-keys', {
          name: 'portal',
          ...(keyAccounts.length > 0 ? { accountIds: keyAccounts } : {}),
        }),
      ),
    onSuccess: async (r) => {
      setNewKey(r.key);
      await qc.invalidateQueries({ queryKey: ['apikeys'] });
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.del(`/portal/api-keys/${id}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['apikeys'] }),
  });
  return (
    <>
      <PageHeader
        title="API & Webhooks"
        subtitle="Connect external systems: receive events by webhook, or integrate with the public API"
      />
      <div className="tabs">
        {(
          [
            ['webhooks', 'Webhooks'],
            ['api', 'API'],
            ['logs', 'API logs'],
          ] as const
        ).map(([k, l]) => (
          <button
            type="button"
            key={k}
            className={tab === k ? 'active' : ''}
            onClick={() => setTab(k)}
          >
            {l}
          </button>
        ))}
      </div>
      {secret ? (
        <div className="alert alert-success">
          Webhook secret (shown once): <code data-testid="webhook-secret">{secret}</code>{' '}
          <button type="button" className="btn btn-sm" onClick={() => setSecret(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {newKey ? (
        <div className="alert alert-success">
          API key (shown once): <code data-testid="api-key">{newKey}</code>{' '}
          <button type="button" className="btn btn-sm" onClick={() => setNewKey(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {tab === 'webhooks' ? (
        <>
          <Card title="Add webhook endpoint">
            <ErrorAlert error={createHook.error as ApiError | null} />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createHook.mutate();
              }}
            >
              <Field label="HTTPS URL">
                <input
                  className="input"
                  placeholder="https://hooks.example.com/autodm"
                  value={hookForm.url}
                  onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })}
                />
              </Field>
              <Field label="Events" group>
                <div className="pill-row">
                  {EVENTS.map((ev) => (
                    <label
                      key={ev}
                      className={`btn btn-sm ${hookForm.events.includes(ev) ? 'btn-primary' : ''}`}
                    >
                      <input
                        type="checkbox"
                        className="pill-input"
                        checked={hookForm.events.includes(ev)}
                        onChange={() =>
                          setHookForm({
                            ...hookForm,
                            events: hookForm.events.includes(ev)
                              ? hookForm.events.filter((x) => x !== ev)
                              : [...hookForm.events, ev],
                          })
                        }
                      />
                      {ev}
                    </label>
                  ))}
                </div>
              </Field>
              <button type="submit" className="btn btn-primary" disabled={createHook.isPending}>
                Create endpoint
              </button>
            </form>
          </Card>
          <Card title="Endpoints">
            <ErrorAlert error={hooks.error} />
            <DataTable>
              <table className="table">
                <thead>
                  <tr>
                    <th>URL</th>
                    <th>Events</th>
                    <th>Accounts</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(hooks.data?.items ?? []).map((h) => (
                    <tr key={h.id}>
                      <td className="mono">{h.url}</td>
                      <td className="pill-row">
                        {h.events.map((e) => (
                          <span key={e} className="badge">
                            {e}
                          </span>
                        ))}
                      </td>
                      <td className="pill-row">
                        {!h.accountIds ? (
                          <span className="muted">All accounts</span>
                        ) : (
                          h.accountIds.map((id) => (
                            <span key={id} className="badge" title={id}>
                              {handleOf(id)}
                            </span>
                          ))
                        )}
                      </td>
                      <td>
                        <Badge value={h.enabled ? 'active' : 'paused'} />
                      </td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setDeliveriesFor(h.id)}
                        >
                          Delivery log
                        </button>{' '}
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => rotate.mutate(h.id)}
                        >
                          Rotate secret
                        </button>{' '}
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => toggleHook.mutate(h)}
                        >
                          {h.enabled ? 'Disable' : 'Enable'}
                        </button>{' '}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteHook.mutate(h.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {hooks.data?.items.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <Empty>No endpoints yet.</Empty>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </DataTable>
          </Card>
        </>
      ) : null}
      {tab === 'api' ? (
        <>
          <Card title="API keys">
            <p className="muted">
              Keys authenticate the public API with <code>Authorization: Bearer adm_live_…</code>.
              Requires the Legend plan or above.
            </p>
            {(accounts.data?.items.length ?? 0) > 0 ? (
              <fieldset className="pill-row" style={{ border: 0, padding: 0, margin: '0 0 8px' }}>
                <legend className="small muted">
                  Account-specific key (leave all unticked for every account you can reach)
                </legend>
                {(accounts.data?.items ?? []).map((a) => (
                  <label key={a.id} className="row" style={{ alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      aria-label={`Key for @${a.username}`}
                      checked={keyAccounts.includes(a.id)}
                      onChange={(e) =>
                        setKeyAccounts((ids) =>
                          e.target.checked ? [...ids, a.id] : ids.filter((x) => x !== a.id),
                        )
                      }
                    />
                    <span className="small">@{a.username}</span>
                  </label>
                ))}
              </fieldset>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => createKey.mutate()}
              disabled={createKey.isPending}
            >
              Generate API key
            </button>
            <ErrorAlert error={keys.error} />
            <table className="table" style={{ marginTop: 12 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Prefix</th>
                  <th>Accounts</th>
                  <th>Created</th>
                  <th>Last used (updated at most once a minute)</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.data?.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="muted">
                      No API keys yet.
                    </td>
                  </tr>
                ) : null}
                {(keys.data?.items ?? []).map((k) => (
                  <tr key={k.id}>
                    <td>{k.name}</td>
                    <td className="mono">{k.prefix}…</td>
                    <td className="pill-row">
                      {k.accountIds ? (
                        k.accountIds.map((id) => (
                          <span key={id} className="badge" title={id}>
                            {handleOf(id)}
                          </span>
                        ))
                      ) : (
                        <span className="muted">All my accounts</span>
                      )}
                    </td>
                    <td>{fmtDate(k.createdAt)}</td>
                    <td>{fmtDate(k.lastUsedAt)}</td>
                    <td>
                      <Badge value={k.revokedAt ? 'revoked' : 'active'} />
                    </td>
                    <td>
                      {k.revokedAt ? null : (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => revoke.mutate(k.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <ApiDocs onPick={setTryEndpoint} />
          <ApiConsole key={newKey ?? 'none'} endpoint={tryEndpoint} initialKey={newKey ?? ''} />
          {sandbox.data ? (
            // DEV-02 (revised 3): only with the fake gateway; a real Instagram app answers 404
            <Card title="Sandbox account">
              <p className="muted">
                Use these details to test your integration without touching a real Instagram
                account.
              </p>
              <pre
                className="mono"
                style={{
                  background: 'var(--surface-2)',
                  padding: 12,
                  borderRadius: 8,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(sandbox.data, null, 2)}
              </pre>
            </Card>
          ) : null}
        </>
      ) : null}
      {tab === 'logs' ? (
        <Card title="Recent API requests">
          {/* UI-48 (revised): a failed log query is said */}
          <ErrorAlert error={logs.error} />
          <DataTable>
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Method</th>
                  <th>Path</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Key</th>
                </tr>
              </thead>
              <tbody>
                {(logs.data?.items ?? []).map((l) => (
                  <tr key={l.id}>
                    <td>{fmtDate(l.createdAt)}</td>
                    <td>{l.method}</td>
                    <td className="mono">{l.path}</td>
                    <td>{l.status}</td>
                    <td>{l.durationMs} ms</td>
                    <td className="mono">{l.apiKeyId ?? '—'}</td>
                  </tr>
                ))}
                {logs.data?.items.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <Empty>No API calls logged yet.</Empty>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </DataTable>
        </Card>
      ) : null}
      {deliveriesFor ? (
        <Modal title="Webhook deliveries" onClose={() => setDeliveriesFor(null)} wide>
          <DataTable>
            <table className="table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Response</th>
                  <th>Error</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(deliveries.data?.items ?? []).map((d) => (
                  <tr key={d.id}>
                    <td>{d.eventType}</td>
                    <td>
                      <Badge value={d.status} />
                    </td>
                    <td>{d.attempts}</td>
                    <td>{d.lastResponseCode ?? '—'}</td>
                    <td className="truncate">{d.lastError ?? '—'}</td>
                    <td>{fmtDate(d.createdAt)}</td>
                    <td className="actions">
                      {d.status === 'failed' ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={retryDelivery.isPending}
                          onClick={() => retryDelivery.mutate(d.id)}
                        >
                          Retry
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {deliveries.data?.items.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <Empty>No deliveries yet.</Empty>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </DataTable>
        </Modal>
      ) : null}
    </>
  );
}
